import { addCalendarDaysYmd, formatWallClockHm, formatWallClockYmd } from './activityDateTime';

export const MAX_RECURRENCE_OCCURRENCES = 90;
export const MAX_RECURRENCE_MONTHS = 12;
export const MAX_RECURRENCE_YEARS = 12;

export type RecurrenceFreq = 'daily' | 'weekly' | 'monthly' | 'yearly';

export type RecurrencePresetId =
  | 'none'
  | 'daily'
  | 'weekly'
  | 'weekdays'
  | 'monthly-bymonthday'
  | 'monthly-byweekday'
  | 'monthly-lastweekday'
  | 'yearly';

export type RecurrenceMonthly =
  | { kind: 'bymonthday'; day: number }
  | { kind: 'byweekday'; weekday: number; nth: number };

export type ActivityRecurrence = {
  freq: RecurrenceFreq;
  interval: number;
  weekdays?: number[];
  monthly?: RecurrenceMonthly;
  until?: string;
  count?: number;
  exdates?: string[];
  weeks?: number;
  months?: number;
};

export type RecurrenceFormState = {
  freq: 'none' | RecurrenceFreq;
  interval: number;
  weekdays: number[];
  monthlyKind: 'bymonthday' | 'byweekday' | 'lastweekday';
  endMode: 'after' | 'until';
  afterCount: number;
  untilDate: string;
};

export type RecurrenceScope = 'this' | 'following' | 'series';

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;
const WEEKDAY_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'] as const;
const MONTH_LONG = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
] as const;
const NTH_LABEL: Record<number, string> = {
  1: '1.',
  2: '2.',
  3: '3.',
  4: '4.',
  5: '5.',
  [-1]: 'letzten',
};
const NTH_WORD: Record<number, string> = {
  1: 'ersten',
  2: 'zweiten',
  3: 'dritten',
  4: 'vierten',
  5: 'fünften',
};

const WORKWEEK_DAYS = [1, 2, 3, 4, 5];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function parseYmdParts(ymd: string): { y: number; mo: number; d: number } | null {
  const m = YMD_RE.exec(String(ymd).trim().slice(0, 10));
  if (!m) return null;
  return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) };
}

export function isoWeekdayFromYmd(ymd: string): number {
  const p = parseYmdParts(ymd);
  if (!p) return 1;
  const js = new Date(Date.UTC(p.y, p.mo - 1, p.d)).getUTCDay();
  return js === 0 ? 7 : js;
}

export function lastDayOfMonth(y: number, mo: number): number {
  return new Date(Date.UTC(y, mo, 0)).getUTCDate();
}

export function ymdFromParts(y: number, mo: number, d: number): string {
  return `${y}-${pad2(mo)}-${pad2(d)}`;
}

export function addCalendarMonthsYmd(ymd: string, months: number): string {
  const p = parseYmdParts(ymd);
  if (!p) return ymd;
  const total = p.y * 12 + (p.mo - 1) + months;
  const ny = Math.floor(total / 12);
  const nmo = (total % 12) + 1;
  const nd = Math.min(p.d, lastDayOfMonth(ny, nmo));
  return ymdFromParts(ny, nmo, nd);
}

export function addCalendarYearsYmd(ymd: string, years: number): string {
  const p = parseYmdParts(ymd);
  if (!p) return ymd;
  const ny = p.y + years;
  const nd = Math.min(p.d, lastDayOfMonth(ny, p.mo));
  return ymdFromParts(ny, p.mo, nd);
}

export function defaultUntilDateForFreq(freq: RecurrenceFreq, startYmd: string): string {
  if (freq === 'yearly') return addCalendarYearsYmd(startYmd, MAX_RECURRENCE_YEARS);
  return addCalendarMonthsYmd(startYmd, MAX_RECURRENCE_MONTHS);
}

function daysBetweenYmd(a: string, b: string): number {
  const pa = parseYmdParts(a);
  const pb = parseYmdParts(b);
  if (!pa || !pb) return 0;
  const ams = Date.UTC(pa.y, pa.mo - 1, pa.d);
  const bms = Date.UTC(pb.y, pb.mo - 1, pb.d);
  return Math.round((bms - ams) / 86400000);
}

function mondayOf(ymd: string): string {
  return addCalendarDaysYmd(ymd, 1 - isoWeekdayFromYmd(ymd));
}

function nthWeekdayOfMonth(y: number, mo: number, weekday: number, nth: number): string | null {
  const last = lastDayOfMonth(y, mo);
  if (nth === -1) {
    for (let d = last; d >= 1; d--) {
      const ymd = ymdFromParts(y, mo, d);
      if (isoWeekdayFromYmd(ymd) === weekday) return ymd;
    }
    return null;
  }
  if (nth < 1 || nth > 5) return null;
  let seen = 0;
  for (let d = 1; d <= last; d++) {
    const ymd = ymdFromParts(y, mo, d);
    if (isoWeekdayFromYmd(ymd) !== weekday) continue;
    seen += 1;
    if (seen === nth) return ymd;
  }
  return null;
}

export function weekdayNthInMonth(ymd: string): { weekday: number; nth: number; isLast: boolean } {
  const p = parseYmdParts(ymd) ?? { y: 1970, mo: 1, d: 1 };
  const weekday = isoWeekdayFromYmd(ymd);
  let nth = 0;
  for (let d = 1; d <= p.d; d++) {
    if (isoWeekdayFromYmd(ymdFromParts(p.y, p.mo, d)) === weekday) nth += 1;
  }
  const last = nthWeekdayOfMonth(p.y, p.mo, weekday, -1);
  return { weekday, nth, isLast: last === ymd };
}

function isWorkweekDays(weekdays: number[] | undefined): boolean {
  if (!weekdays || weekdays.length !== WORKWEEK_DAYS.length) return false;
  const sorted = [...weekdays].sort((a, b) => a - b);
  return WORKWEEK_DAYS.every((d, i) => sorted[i] === d);
}

function uniqueWeekdays(values: unknown): number[] {
  const out: number[] = [];
  if (!Array.isArray(values)) return out;
  for (const raw of values) {
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > 7) continue;
    if (!out.includes(n)) out.push(n);
  }
  return out.sort((a, b) => a - b);
}

function parseMonthly(raw: unknown): RecurrenceMonthly | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const kind = String(o.kind ?? '');
  if (kind === 'bymonthday') {
    const day = Number(o.day);
    if (day === -1 || (Number.isInteger(day) && day >= 1 && day <= 31)) {
      return { kind: 'bymonthday', day };
    }
    return undefined;
  }
  if (kind === 'byweekday') {
    const weekday = Number(o.weekday);
    const nth = Number(o.nth);
    if (!Number.isInteger(weekday) || weekday < 1 || weekday > 7) return undefined;
    if (nth !== -1 && !(Number.isInteger(nth) && nth >= 1 && nth <= 5)) return undefined;
    return { kind: 'byweekday', weekday, nth };
  }
  return undefined;
}

export function parseActivityRecurrence(raw: unknown): { value: ActivityRecurrence } | { error: string } {
  if (raw == null || raw === '') return { error: 'Wiederholung fehlt.' };
  if (typeof raw !== 'object') return { error: 'Ungültige Wiederholung.' };
  const o = raw as Record<string, unknown>;
  const freq = String(o.freq ?? '');
  if (freq !== 'daily' && freq !== 'weekly' && freq !== 'monthly' && freq !== 'yearly') {
    return { error: 'Ungültige Wiederholungsfrequenz.' };
  }
  const interval = Number(o.interval ?? 1);
  if (!Number.isInteger(interval) || interval < 1 || interval > 99) {
    return { error: 'Ungültiges Wiederholungsintervall.' };
  }
  const until = o.until == null || o.until === '' ? undefined : String(o.until).trim().slice(0, 10);
  if (until && !parseYmdParts(until)) return { error: 'Ungültiges Enddatum der Wiederholung.' };
  const count = o.count == null || o.count === '' ? undefined : Number(o.count);
  if (count !== undefined && (!Number.isInteger(count) || count < 1)) {
    return { error: 'Ungültige Anzahl an Terminen.' };
  }
  if (until && count !== undefined) {
    return { error: 'Ende als Datum und als Anzahl dürfen nicht kombiniert werden.' };
  }
  const weekdays = uniqueWeekdays(o.weekdays);
  if (freq === 'weekly' && weekdays.length === 0) {
    return { error: 'Mindestens einen Wochentag wählen.' };
  }
  const monthly = parseMonthly(o.monthly);
  if (freq === 'monthly' && !monthly) {
    return { error: 'Monatliche Wiederholung unvollständig.' };
  }
  const exdates = Array.isArray(o.exdates)
    ? o.exdates.map((d) => String(d).trim().slice(0, 10)).filter((d) => Boolean(parseYmdParts(d)))
    : [];
  const weeks = o.weeks == null || o.weeks === '' ? undefined : Number(o.weeks);
  const months = o.months == null || o.months === '' ? undefined : Number(o.months);
  const value: ActivityRecurrence = {
    freq,
    interval,
    ...(freq === 'weekly' ? { weekdays } : {}),
    ...(freq === 'monthly' && monthly ? { monthly } : {}),
    ...(until ? { until } : {}),
    ...(count !== undefined ? { count } : {}),
    ...(exdates.length ? { exdates } : {}),
    ...(Number.isInteger(weeks) && (weeks as number) > 0 ? { weeks: weeks as number } : {}),
    ...(Number.isInteger(months) && (months as number) > 0 ? { months: months as number } : {}),
  };
  return { value };
}

function matchesRule(ymd: string, startYmd: string, rec: ActivityRecurrence): boolean {
  if (ymd < startYmd) return false;
  if (rec.freq === 'daily') {
    const diff = daysBetweenYmd(startYmd, ymd);
    return diff >= 0 && diff % rec.interval === 0;
  }
  if (rec.freq === 'weekly') {
    const days = rec.weekdays ?? [];
    if (!days.includes(isoWeekdayFromYmd(ymd))) return false;
    const weekDelta = Math.round(daysBetweenYmd(mondayOf(startYmd), mondayOf(ymd)) / 7);
    return weekDelta >= 0 && weekDelta % rec.interval === 0;
  }
  if (rec.freq === 'yearly') {
    const start = parseYmdParts(startYmd);
    const cur = parseYmdParts(ymd);
    if (!start || !cur) return false;
    const yearDelta = cur.y - start.y;
    if (yearDelta < 0 || yearDelta % rec.interval !== 0) return false;
    if (cur.mo !== start.mo) return false;
    if (start.d > lastDayOfMonth(cur.y, cur.mo)) return false;
    return cur.d === start.d;
  }
  const start = parseYmdParts(startYmd);
  const cur = parseYmdParts(ymd);
  if (!start || !cur || !rec.monthly) return false;
  const monthDelta = cur.y * 12 + cur.mo - (start.y * 12 + start.mo);
  if (monthDelta < 0 || monthDelta % rec.interval !== 0) return false;
  if (rec.monthly.kind === 'bymonthday') {
    if (rec.monthly.day === -1) return cur.d === lastDayOfMonth(cur.y, cur.mo);
    if (rec.monthly.day > lastDayOfMonth(cur.y, cur.mo)) return false;
    return cur.d === rec.monthly.day;
  }
  const slot = nthWeekdayOfMonth(cur.y, cur.mo, rec.monthly.weekday, rec.monthly.nth);
  return slot === ymd;
}

export function expandOccurrenceYmds(
  rec: ActivityRecurrence,
  startYmd: string,
): { dates: string[] } | { error: string } {
  const start = parseYmdParts(startYmd);
  if (!start) return { error: 'Ungültiges Startdatum.' };
  const hardUntil =
    rec.freq === 'yearly'
      ? addCalendarYearsYmd(startYmd, MAX_RECURRENCE_YEARS)
      : addCalendarMonthsYmd(startYmd, MAX_RECURRENCE_MONTHS);
  const until = rec.until && rec.until < hardUntil ? rec.until : hardUntil;
  const ex = new Set(rec.exdates ?? []);
  const dates: string[] = [];
  let cursor = startYmd;
  let guard = 0;
  const maxGuard = rec.freq === 'yearly' ? 4500 : 800;
  while (cursor <= until && dates.length < MAX_RECURRENCE_OCCURRENCES && guard < maxGuard) {
    guard += 1;
    if (rec.count != null && dates.length >= rec.count) break;
    if (!ex.has(cursor) && matchesRule(cursor, startYmd, rec)) dates.push(cursor);
    cursor = addCalendarDaysYmd(cursor, 1);
  }
  if (dates.length === 0) return { error: 'Die Wiederholung erzeugt keine Termine.' };
  return { dates };
}

/** Erster Termin nach `afterYmd` (exklusiv) gemäß Regel ab `anchorYmd`; `null`, wenn die Serie vorher endet. */
export function nextOccurrenceYmd(rec: ActivityRecurrence, anchorYmd: string, afterYmd: string): string | null {
  if (!parseYmdParts(anchorYmd) || !parseYmdParts(afterYmd)) return null;
  const ex = new Set(rec.exdates ?? []);
  let cursor = addCalendarDaysYmd(afterYmd < anchorYmd ? anchorYmd : afterYmd, afterYmd < anchorYmd ? 0 : 1);
  // Jährlich am 29.02. trifft nur Schaltjahre — 8 Jahre Puffer reichen auch mit Intervall.
  for (let guard = 0; guard < 3000 * Math.max(1, rec.interval); guard++) {
    if (rec.until && cursor > rec.until) return null;
    if (!ex.has(cursor) && matchesRule(cursor, anchorYmd, rec)) return cursor;
    cursor = addCalendarDaysYmd(cursor, 1);
  }
  return null;
}

export function withExdate(rec: ActivityRecurrence, ymd: string): ActivityRecurrence {
  const set = new Set(rec.exdates ?? []);
  set.add(ymd);
  return { ...rec, exdates: [...set].sort() };
}

export function withoutExdates(rec: ActivityRecurrence, ymds: Iterable<string>): ActivityRecurrence {
  const drop = new Set(ymds);
  const exdates = (rec.exdates ?? []).filter((d) => !drop.has(d));
  const next: ActivityRecurrence = { ...rec, exdates };
  if (exdates.length === 0) delete next.exdates;
  return next;
}

export function truncateUntilBefore(rec: ActivityRecurrence, splitYmd: string): ActivityRecurrence {
  const until = addCalendarDaysYmd(splitYmd, -1);
  const next: ActivityRecurrence = { ...rec, until };
  delete next.count;
  return next;
}

export function recurrencePatternKey(rec: ActivityRecurrence): string {
  return JSON.stringify({
    freq: rec.freq,
    interval: rec.interval,
    weekdays: rec.weekdays ?? [],
    monthly: rec.monthly ?? null,
    until: rec.until ?? null,
    count: rec.count ?? null,
  });
}

export function parseRecurrenceScope(raw: unknown, fallback: RecurrenceScope = 'this'): RecurrenceScope {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'this' || v === 'following' || v === 'series') return v;
  if (v === 'all') return 'series';
  return fallback;
}

function sameInstant(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const da = new Date(String(a)).getTime();
  const db = new Date(String(b)).getTime();
  if (Number.isNaN(da) || Number.isNaN(db)) return String(a) === String(b);
  return da === db;
}

export function patchMovesOccurrenceTime(
  patch: Record<string, unknown>,
  existing: { due_at?: unknown; end_at?: unknown },
): boolean {
  if (patch.due_at !== undefined && !sameInstant(patch.due_at, existing.due_at)) return true;
  if (patch.end_at !== undefined && !sameInstant(patch.end_at, existing.end_at)) return true;
  return false;
}

function weekdayListLabel(weekdays: number[]): string {
  return weekdays.map((d) => WEEKDAY_SHORT[d - 1] ?? '').filter(Boolean).join(', ');
}

function monthlyLabel(monthly: RecurrenceMonthly): string {
  if (monthly.kind === 'bymonthday') {
    if (monthly.day === -1) return 'am letzten Tag';
    return `am ${monthly.day}.`;
  }
  const day = WEEKDAY_LONG[monthly.weekday - 1] ?? '';
  if (monthly.nth === -1) return `letzten ${day}`;
  return `${NTH_WORD[monthly.nth] ?? NTH_LABEL[monthly.nth] ?? `${monthly.nth}.`} ${day}`;
}

function yearlyOnLabel(startYmd: string | undefined): string {
  const p = startYmd ? parseYmdParts(startYmd) : null;
  if (!p) return 'Jährlich';
  return `Jährlich am ${p.d}. ${MONTH_LONG[p.mo - 1]}`;
}

function intervalPrefix(freq: RecurrenceFreq, interval: number): string {
  if (interval <= 1) {
    if (freq === 'daily') return 'Täglich';
    if (freq === 'weekly') return '';
    if (freq === 'yearly') return 'Jährlich';
    return 'Monatlich';
  }
  if (freq === 'daily') return `Alle ${interval} Tage`;
  if (freq === 'weekly') return `Alle ${interval} Wochen`;
  if (freq === 'yearly') return `Alle ${interval} Jahre`;
  return `Jeden ${interval}. Monat`;
}

function endLabel(rec: ActivityRecurrence): string | null {
  if (rec.weeks && rec.freq === 'weekly') {
    const n = rec.weeks;
    return n === 1 ? '1 Woche' : `${n} Wochen`;
  }
  if (rec.months && rec.freq === 'monthly') {
    const n = rec.months;
    return n === 1 ? '1 Monat' : `${n} Monate`;
  }
  if (rec.count) return rec.count === 1 ? '1 Termin' : `${rec.count} Termine`;
  if (rec.until) {
    const p = parseYmdParts(rec.until);
    if (!p) return `bis ${rec.until}`;
    const formatted = new Date(Date.UTC(p.y, p.mo - 1, p.d)).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    });
    return `bis ${formatted}`;
  }
  return null;
}

export function formatRecurrenceLabel(
  rec: ActivityRecurrence,
  times?: { start?: string | null; end?: string | null },
  startYmd?: string,
): string {
  const parts: string[] = [];
  if (rec.freq === 'weekly') {
    const ids = rec.weekdays ?? [];
    const days =
      ids.length === 1
        ? WEEKDAY_LONG[ids[0] - 1] ?? weekdayListLabel(ids)
        : weekdayListLabel(ids);
    const every = intervalPrefix('weekly', rec.interval);
    if (every) parts.push(`${every} ${days}`.trim());
    else if (isWorkweekDays(ids)) parts.push('Jeden Montag bis Freitag');
    else parts.push(ids.length === 1 ? `Wöchentlich am ${days}` : days || 'Wöchentlich');
  } else if (rec.freq === 'monthly' && rec.monthly) {
    const monthly = monthlyLabel(rec.monthly);
    if (rec.monthly.kind === 'byweekday' && rec.interval <= 1) {
      parts.push(`Monatlich am ${monthly}`);
    } else if (rec.monthly.kind === 'bymonthday' && rec.interval <= 1) {
      parts.push(`Monatlich ${monthly}`);
    } else {
      parts.push(`${intervalPrefix('monthly', rec.interval)} ${monthly}`.replace(/\s+/g, ' ').trim());
    }
  } else if (rec.freq === 'yearly') {
    const yearly = yearlyOnLabel(startYmd);
    if (rec.interval > 1) parts.push(`Alle ${rec.interval} Jahre am ${yearly.replace(/^Jährlich am /, '')}`);
    else parts.push(yearly);
  } else {
    parts.push(intervalPrefix('daily', rec.interval));
  }
  const start = times?.start?.trim();
  const end = times?.end?.trim();
  if (start && end) parts.push(`${start}–${end}`);
  else if (start) parts.push(start);
  const ending = endLabel(rec);
  if (ending) parts.push(ending);
  return parts.filter(Boolean).join(' · ');
}

export function formatRecurrenceLabelFromIso(params: {
  rec: ActivityRecurrence;
  dueAt?: string | null;
  endAt?: string | null;
  timeZone: string;
}): string {
  const start = params.dueAt ? formatWallClockHm(new Date(params.dueAt), params.timeZone) : null;
  const end = params.endAt ? formatWallClockHm(new Date(params.endAt), params.timeZone) : null;
  const startYmd = params.dueAt ? formatWallClockYmd(new Date(params.dueAt), params.timeZone) : undefined;
  return formatRecurrenceLabel(params.rec, { start, end }, startYmd);
}

export function defaultRecurrenceForm(startYmd: string): RecurrenceFormState {
  return {
    freq: 'none',
    interval: 1,
    weekdays: [isoWeekdayFromYmd(startYmd)],
    monthlyKind: 'byweekday',
    endMode: 'until',
    afterCount: 3,
    untilDate: defaultUntilDateForFreq('monthly', startYmd),
  };
}

export function recurrenceFormFromStored(
  rec: ActivityRecurrence | null | undefined,
  startYmd: string,
): RecurrenceFormState {
  const base = defaultRecurrenceForm(startYmd);
  if (!rec) return base;
  const untilFromWeeks = rec.weeks ? addCalendarDaysYmd(startYmd, rec.weeks * 7 - 1) : null;
  const untilFromMonths = rec.months
    ? (() => {
        const start = parseYmdParts(startYmd);
        if (!start) return null;
        const endMonth = addCalendarMonthsYmd(ymdFromParts(start.y, start.mo, 1), rec.months - 1);
        const end = parseYmdParts(endMonth);
        return end ? ymdFromParts(end.y, end.mo, lastDayOfMonth(end.y, end.mo)) : null;
      })()
    : null;
  return {
    freq: rec.freq,
    interval: rec.interval,
    weekdays: rec.weekdays?.length ? rec.weekdays : base.weekdays,
    monthlyKind:
      rec.monthly?.kind === 'byweekday' && rec.monthly.nth === -1
        ? 'lastweekday'
        : rec.monthly?.kind === 'byweekday'
          ? 'byweekday'
          : rec.monthly?.kind === 'bymonthday'
            ? 'bymonthday'
            : 'byweekday',
    endMode: 'until',
    afterCount: rec.count ?? rec.weeks ?? rec.months ?? base.afterCount,
    untilDate: rec.until ?? untilFromWeeks ?? untilFromMonths ?? defaultUntilDateForFreq(rec.freq, startYmd),
  };
}

export function monthlyChoicesFromStart(startYmd: string): {
  bymonthday: RecurrenceMonthly;
  byweekday: RecurrenceMonthly;
  lastWeekday: RecurrenceMonthly | null;
  day: number;
  weekdayLabel: string;
  nthLabel: string;
} {
  const p = parseYmdParts(startYmd) ?? { y: 1970, mo: 1, d: 1 };
  const { weekday, nth, isLast } = weekdayNthInMonth(startYmd);
  return {
    bymonthday: { kind: 'bymonthday', day: p.d },
    byweekday: { kind: 'byweekday', weekday, nth },
    lastWeekday: isLast ? { kind: 'byweekday', weekday, nth: -1 } : null,
    day: p.d,
    weekdayLabel: WEEKDAY_LONG[weekday - 1] ?? '',
    nthLabel: NTH_WORD[nth] ?? NTH_LABEL[nth] ?? `${nth}.`,
  };
}

export function recurrencePresetId(form: RecurrenceFormState, startYmd: string): RecurrencePresetId {
  if (form.freq === 'none') return 'none';
  if (form.freq === 'daily') return 'daily';
  if (form.freq === 'weekly') return isWorkweekDays(form.weekdays) ? 'weekdays' : 'weekly';
  if (form.freq === 'yearly') return 'yearly';
  const choices = monthlyChoicesFromStart(startYmd);
  if (form.monthlyKind === 'lastweekday' && choices.lastWeekday) return 'monthly-lastweekday';
  if (form.monthlyKind === 'bymonthday') return 'monthly-bymonthday';
  return 'monthly-byweekday';
}

export function recurrencePresetOptions(
  startYmd: string,
  current?: RecurrencePresetId,
): { value: RecurrencePresetId; label: string }[] {
  const p = parseYmdParts(startYmd) ?? { y: 1970, mo: 1, d: 1 };
  const choices = monthlyChoicesFromStart(startYmd);
  const weekday = choices.weekdayLabel;
  const options: { value: RecurrencePresetId; label: string }[] = [
    { value: 'none', label: 'Keine Wiederholung' },
    { value: 'daily', label: 'Täglich' },
    { value: 'weekly', label: `Wöchentlich am ${weekday}` },
    { value: 'weekdays', label: 'Jeden Montag bis Freitag' },
  ];
  if (current === 'monthly-bymonthday') {
    options.push({ value: 'monthly-bymonthday', label: `Monatlich am ${choices.day}.` });
  }
  options.push({
    value: 'monthly-byweekday',
    label: `Monatlich am ${choices.nthLabel} ${weekday}`,
  });
  if (choices.lastWeekday) {
    options.push({
      value: 'monthly-lastweekday',
      label: `Monatlich am letzten ${weekday}`,
    });
  }
  options.push({
    value: 'yearly',
    label: `Jährlich am ${p.d}. ${MONTH_LONG[p.mo - 1]}`,
  });
  return options;
}

export function applyRecurrencePreset(
  preset: RecurrencePresetId,
  startYmd: string,
  form: RecurrenceFormState,
): RecurrenceFormState {
  if (preset === 'none') {
    return { ...form, freq: 'none', endMode: 'until' };
  }
  const freq: RecurrenceFreq =
    preset === 'daily'
      ? 'daily'
      : preset === 'weekly' || preset === 'weekdays'
        ? 'weekly'
        : preset === 'yearly'
          ? 'yearly'
          : 'monthly';
  const monthlyKind: RecurrenceFormState['monthlyKind'] =
    preset === 'monthly-lastweekday'
      ? 'lastweekday'
      : preset === 'monthly-bymonthday'
        ? 'bymonthday'
        : 'byweekday';
  let untilDate = form.untilDate;
  const switchingOn = form.freq === 'none' || !parseYmdParts(untilDate) || untilDate < startYmd;
  if (switchingOn) untilDate = defaultUntilDateForFreq(freq, startYmd);
  if (freq === 'yearly') {
    const yearlyUntil = defaultUntilDateForFreq('yearly', startYmd);
    if (!untilDate || untilDate < yearlyUntil) untilDate = yearlyUntil;
  }
  return {
    ...form,
    freq,
    interval: 1,
    monthlyKind,
    weekdays: preset === 'weekdays' ? [...WORKWEEK_DAYS] : [isoWeekdayFromYmd(startYmd)],
    endMode: 'until',
    untilDate,
  };
}

function spanCrossesWeekend(startYmd: string, endYmd: string): boolean {
  const days = daysBetweenYmd(startYmd, endYmd);
  if (days >= 6) return true;
  for (let i = 0; i <= days; i += 1) {
    if (isoWeekdayFromYmd(addCalendarDaysYmd(startYmd, i)) >= 6) return true;
  }
  return false;
}

/**
 * Termin über mehr als 24 Stunden (Ende-Datum nach dem Start-Datum und Bis nach Von) → Serie bis
 * zum Ende-Datum, jede Einheit von Von bis Bis am selben Tag. Liegt ein Wochenende dazwischen,
 * Start und Ende aber auf Werktagen, wird Mo–Fr gewählt, sonst täglich. Kürzere Spannen über
 * Mitternacht (Nachtschicht) bleiben ein durchgehender Block. `null` = nichts umzustellen.
 */
export function recurrenceForMultiDaySpan(params: {
  startYmd: string;
  endYmd: string;
  startTime: string;
  endTime: string;
  form: RecurrenceFormState;
}): RecurrenceFormState | null {
  const { startYmd, endYmd, startTime, endTime, form } = params;
  if (form.freq !== 'none') return null;
  if (!parseYmdParts(startYmd) || !parseYmdParts(endYmd) || endYmd <= startYmd) return null;
  if (!startTime || !endTime || endTime <= startTime) return null;
  const weekdaysOnly =
    isoWeekdayFromYmd(startYmd) <= 5 && isoWeekdayFromYmd(endYmd) <= 5 && spanCrossesWeekend(startYmd, endYmd);
  return { ...applyRecurrencePreset(weekdaysOnly ? 'weekdays' : 'daily', startYmd, form), untilDate: endYmd };
}

/**
 * Automatisch angelegte Serie: `{ endYmd }` merkt sich das eigentliche Ende-Datum, damit
 * „Keine Wiederholung“ oder eine geänderte Uhrzeit wieder einen durchgehenden Termin ergibt.
 * `'declined'` = Nutzer hat die automatische Serie abgewählt; dann nicht erneut umstellen.
 */
export type MultiDaySeriesState = { endYmd: string } | 'declined' | null;

type MultiDayFormFields = {
  dueDate: string;
  dueTimeStart: string;
  dueTimeEnd: string;
  endDate: string;
  recurrence?: RecurrenceFormState;
  multiDaySeries?: MultiDaySeriesState;
};

export function autoMultiDaySeriesEnd(f: MultiDayFormFields): string | null {
  const s = f.multiDaySeries;
  return s && s !== 'declined' && f.recurrence && f.recurrence.freq !== 'none' ? s.endYmd : null;
}

/**
 * Nach jeder Änderung an Datum/Uhrzeit: mehrtägige Termine (> 24 h) werden zur Serie, eine
 * automatische Serie wird wieder zum durchgehenden Termin, sobald die Spanne nicht mehr passt.
 * Vom Nutzer gewählte Serien bleiben unberührt.
 */
export function reconcileMultiDaySeries<T extends MultiDayFormFields>(f: T): T {
  const rec = f.recurrence ?? defaultRecurrenceForm(f.dueDate);
  const autoEnd = autoMultiDaySeriesEnd(f);
  if (!autoEnd && (rec.freq !== 'none' || f.multiDaySeries === 'declined')) return f;
  const endYmd = autoEnd ?? f.endDate;
  const series = recurrenceForMultiDaySpan({
    startYmd: f.dueDate,
    endYmd,
    startTime: f.dueTimeStart,
    endTime: f.dueTimeEnd,
    form: { ...rec, freq: 'none' },
  });
  if (series) return { ...f, recurrence: series, endDate: f.dueDate, multiDaySeries: { endYmd } };
  if (!autoEnd) return f;
  return {
    ...f,
    recurrence: { ...rec, freq: 'none' },
    endDate: endYmd < f.dueDate ? f.dueDate : endYmd,
    multiDaySeries: null,
  };
}

/** Wiederholung im Dropdown geändert: Abwählen der automatischen Serie stellt das Ende-Datum wieder her. */
export function applyRecurrenceChoice<T extends MultiDayFormFields>(f: T, next: RecurrenceFormState): T {
  const autoEnd = autoMultiDaySeriesEnd(f);
  if (next.freq === 'none') {
    return autoEnd
      ? { ...f, recurrence: next, endDate: autoEnd, multiDaySeries: 'declined' }
      : { ...f, recurrence: next };
  }
  return { ...f, recurrence: next, endDate: f.dueDate, multiDaySeries: null };
}

export function buildRecurrenceFromForm(
  form: RecurrenceFormState,
  startYmd: string,
): { value: ActivityRecurrence } | { error: string } | { value: null } {
  if (form.freq === 'none') return { value: null };
  const interval = Math.max(1, Math.floor(Number(form.interval) || 1));
  const rec: ActivityRecurrence = { freq: form.freq, interval };
  if (form.freq === 'weekly') {
    rec.weekdays = isWorkweekDays(form.weekdays) ? [...WORKWEEK_DAYS] : [isoWeekdayFromYmd(startYmd)];
  }
  if (form.freq === 'monthly') {
    const choices = monthlyChoicesFromStart(startYmd);
    if (form.monthlyKind === 'lastweekday') {
      rec.monthly = choices.lastWeekday ?? { kind: 'byweekday', weekday: isoWeekdayFromYmd(startYmd), nth: -1 };
    } else if (form.monthlyKind === 'byweekday') {
      rec.monthly = choices.byweekday;
    } else {
      rec.monthly = choices.bymonthday;
    }
  }
  const until = String(form.untilDate || '').slice(0, 10);
  if (!parseYmdParts(until)) return { error: 'Enddatum der Wiederholung wählen.' };
  rec.until = until;
  return parseActivityRecurrence(rec);
}

export type ProjectActivityListItem = Record<string, unknown> & {
  id: string;
  series_id?: string | null;
  recurrence?: ActivityRecurrence | null;
  recurrence_label?: string | null;
  occurrence_count?: number;
  done_count?: number;
  occurrence_ids?: string[];
  is_series?: boolean;
  done?: boolean;
  due_at?: string | null;
  original_start_at?: string | null;
};

function occurrenceSortKey(row: Record<string, unknown>): string {
  return String(row.original_start_at || row.due_at || row.created_at || '');
}

export function collapseActivitiesForProjectList(
  rows: Record<string, unknown>[],
  timeZone: string,
): ProjectActivityListItem[] {
  const standalone: ProjectActivityListItem[] = [];
  const seriesMap = new Map<string, Record<string, unknown>[]>();
  for (const row of rows) {
    const seriesId = row.series_id != null && String(row.series_id).trim() ? String(row.series_id) : '';
    if (!seriesId) {
      standalone.push(row as ProjectActivityListItem);
      continue;
    }
    const list = seriesMap.get(seriesId) ?? [];
    list.push(row);
    seriesMap.set(seriesId, list);
  }
  const seriesItems: ProjectActivityListItem[] = [];
  for (const [seriesId, members] of seriesMap) {
    const sorted = [...members].sort((a, b) => occurrenceSortKey(a).localeCompare(occurrenceSortKey(b)));
    const first = sorted[0] as ProjectActivityListItem;
    const parsed = parseActivityRecurrence(first.recurrence);
    const rec = 'value' in parsed ? parsed.value : null;
    const occurrenceIds = sorted.map((m) => String(m.id));
    const doneCount = sorted.filter((m) => Boolean(m.done)).length;
    const label = rec
      ? formatRecurrenceLabelFromIso({
          rec,
          dueAt: first.due_at != null ? String(first.due_at) : null,
          endAt: first.end_at != null ? String(first.end_at) : null,
          timeZone,
        })
      : null;
    seriesItems.push({
      ...first,
      series_id: seriesId,
      recurrence: rec,
      recurrence_label: label,
      occurrence_count: sorted.length,
      done_count: doneCount,
      occurrence_ids: occurrenceIds,
      is_series: true,
      done: doneCount === sorted.length && sorted.length > 0,
    });
  }
  return [...standalone, ...seriesItems].sort((a, b) => {
    const aDone = Boolean(a.done);
    const bDone = Boolean(b.done);
    if (aDone !== bDone) return aDone ? 1 : -1;
    return String(b.created_at ?? '').localeCompare(String(a.created_at ?? ''));
  });
}

export function originalStartYmd(row: Record<string, unknown>, timeZone: string): string | null {
  const iso = row.original_start_at || row.due_at;
  if (!iso) return null;
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return null;
  return formatWallClockYmd(d, timeZone);
}
